<template>
	<v-card data-testid = "dashboard-kpi-events-widget" dark outlined class = "dashboard-kpi-events-widget fill-height" :class = "[severityClass, { 'dashboard-kpi-events-widget--critical-pulse': isCriticalSeverity }]" :style = "widgetStyle">
		<v-system-bar lights-out dark class = "vue-draggable-handle" style = "cursor:move">
			<v-icon small color = "light-blue lighten-2">mdi-counter</v-icon>
			<span class = "white--text subtitle-2 text-uppercase ml-1 text-truncate">{{ widgetTitle }}</span>
			<v-spacer></v-spacer>
			<v-btn icon x-small class = "no-drag" :disabled = "loading" @click = "refresh">
				<v-icon x-small color = "white">mdi-refresh</v-icon>
			</v-btn>
			<v-btn icon x-small class = "error ml-1 no-drag" @click = "$emit('close_component', layout_id)">
				<v-icon x-small color = "white">mdi-close</v-icon>
			</v-btn>
		</v-system-bar>

		<div class = "dashboard-kpi-events-widget__body no-drag">
			<div class = "dashboard-kpi-events-widget__intervals">
				<v-btn-toggle v-model = "selectedInterval" mandatory dense dark class = "dashboard-kpi-events-widget__interval-toggle">
					<v-btn v-for = "option in intervalOptions" :key = "option.value" x-small :value = "option.value" :disabled = "loading">
						{{ $ml.get(option.labelKey) }}
					</v-btn>
				</v-btn-toggle>
			</div>

			<v-overlay v-if = "loading && !hasValue" absolute z-index = "10">
				<v-progress-circular indeterminate size = "40" color = "light-blue lighten-2"></v-progress-circular>
			</v-overlay>

			<v-alert v-if = "unsupportedSchema" type = "warning" dense prominent class = "ma-2">
				{{ $ml.get('dashboard_pie_events_unsupported_config') }}
			</v-alert>

			<v-alert v-else-if = "error" type = "error" dense prominent class = "ma-2">
				{{ error }}
			</v-alert>

			<div v-else class = "dashboard-kpi-events-widget__content">
				<div class = "dashboard-kpi-events-widget__icon">
					<v-icon color = "white" large>{{ appearanceIcon }}</v-icon>
				</div>
				<div class = "dashboard-kpi-events-widget__metric">
					<div class = "caption text-uppercase dashboard-kpi-events-widget__label">{{ $ml.get('dashboard_pie_events_interval_total') }}</div>
					<strong data-testid = "dashboard-kpi-events-widget-value">{{ formattedMetricValue }}</strong>
					<div v-if = "intervalDisplay" data-testid = "dashboard-kpi-events-widget-interval" class = "caption dashboard-kpi-events-widget__meta">
						{{ $ml.get('dashboard_pie_events_section_interval') }}: {{ intervalDisplay }}
					</div>
					<div class = "caption dashboard-kpi-events-widget__meta">
						{{ $ml.get('dashboard_pie_events_refresh_seconds') }}: {{ refreshSeconds }}
					</div>
				</div>
			</div>
		</div>
	</v-card>
</template>

<script>
import DashboardChartsService from '@/services/dashboard-charts.service.js';
import {
	DASHBOARD_PIE_EVENTS_SCHEMA_VERSION,
	DASHBOARD_PIE_EVENTS_DEFAULT_REFRESH_SECONDS,
	DASHBOARD_PIE_EVENTS_DEFAULT_KPI_APPEARANCE,
	DASHBOARD_CHART_INTERVAL_OPTIONS,
	buildDashboardChartTimeRange,
	normalizeDashboardChartIntervalPreset,
} from '@/services/dashboard-pie-events.config.js';

export default {
	name : 'dashboard_kpi_events_widget',
	props : ['layout', 'layout_id', 'hosts', 'info_to_component', 'layout_end'],
	data(){
		return {
			metricValue : 0,
			metricData : null,
			loading : false,
			error : null,
			refreshTimer : null,
			selectedInterval : this.getInitialInterval(),
		};
	},
	computed : {
		intervalOptions(){
			if (this.isConfiguredCustomInterval()) {
				return DASHBOARD_CHART_INTERVAL_OPTIONS.concat([{ value: 'custom', labelKey: 'dashboard_pie_events_preset_custom' }]);
			}
			return DASHBOARD_CHART_INTERVAL_OPTIONS;
		},
		streamData(){
			return this.info_to_component || this.hosts || {};
		},
		timezone(){
			const config = this.streamData && this.streamData.config ? this.streamData.config : {};
			return config.time && config.time.custom && config.time.custom.tz ? config.time.custom.tz : 'America/Lima';
		},
		requestStreamData(){
			const config = this.streamData && this.streamData.config ? this.streamData.config : {};
			const time = this.selectedInterval === 'custom' && this.isConfiguredCustomInterval()
				? Object.assign({}, config.time, { custom: Object.assign({}, config.time.custom) })
				: buildDashboardChartTimeRange(this.selectedInterval, this.timezone);
			return Object.assign({}, this.streamData, {
				config: Object.assign({}, config, {
					time,
				}),
			});
		},
		unsupportedSchema(){
			return !this.streamData || this.streamData.schemaVersion !== DASHBOARD_PIE_EVENTS_SCHEMA_VERSION;
		},
		widgetTitle(){
			const config = this.streamData && this.streamData.config ? this.streamData.config : {};
			const titleKey = (this.streamData && this.streamData.titleKey) || config.titleKey;
			if (titleKey) return this.$ml.get(titleKey);
			return (this.streamData && this.streamData.title) || config.name || config.title || this.$ml.get('dashboard_pie_events_chart_type_kpi');
		},
		refreshSeconds(){
			const config = this.streamData && this.streamData.config ? this.streamData.config : {};
			const value = Number(config.refreshSeconds || this.streamData.refreshSeconds || DASHBOARD_PIE_EVENTS_DEFAULT_REFRESH_SECONDS);
			return value > 0 ? value : DASHBOARD_PIE_EVENTS_DEFAULT_REFRESH_SECONDS;
		},
		appearance(){
			const config = this.streamData && this.streamData.config ? this.streamData.config : {};
			const source = config.appearance || this.streamData.appearance || {};
			return {
				color: source.color || DASHBOARD_PIE_EVENTS_DEFAULT_KPI_APPEARANCE.color,
				icon: source.icon || DASHBOARD_PIE_EVENTS_DEFAULT_KPI_APPEARANCE.icon,
			};
		},
		appearanceIcon(){
			return this.appearance.icon;
		},
		metricFormat(){
			const config = this.streamData && this.streamData.config ? this.streamData.config : {};
			return config.metricFormat || config.metric_format || this.streamData.metricFormat || this.streamData.metric_format || null;
		},
		metricKey(){
			const config = this.streamData && this.streamData.config ? this.streamData.config : {};
			return config.metric || config.metricKey || config.kpiPreset || this.streamData.metric || this.streamData.metricKey || null;
		},
		formattedMetricValue(){
			if (this.metricFormat === 'percentage') {
				return this.formatPercentage(this.metricValue);
			}
			return this.metricValue;
		},
		metricSeverity(){
			return this.getMetricSeverity(this.metricKey, this.metricValue, this.metricData);
		},
		severityClass(){
			return `dashboard-kpi-events-widget--severity-${this.metricSeverity}`;
		},
		isCriticalSeverity(){
			return this.metricSeverity === 'critical';
		},
		severityColor(){
			const colors = {
				healthy: '#2E7D32',
				warning: '#F9A825',
				danger: '#EF6C00',
				critical: '#C62828',
				neutral: this.appearance.color,
			};
			return colors[this.metricSeverity] || this.appearance.color;
		},
		intervalDisplay(){
			const preset = this.selectedInterval;
			const config = this.requestStreamData && this.requestStreamData.config ? this.requestStreamData.config : this.streamData || {};
			const time = config.time || config.interval || {};
			const custom = time.custom || {};

			if (time.labelKey || time.displayKey || time.presetLabelKey) {
				return this.$ml.get(time.labelKey || time.displayKey || time.presetLabelKey);
			}

			if (preset === 'custom' && custom.from && custom.to) {
				return `${custom.from} - ${custom.to}`;
			}

			if (preset) {
				return this.$ml.get(`dashboard_pie_events_preset_${preset}`);
			}

			return null;
		},
		widgetStyle(){
			return {
				background: `linear-gradient(135deg, ${this.severityColor}, #0F253D)`,
			};
		},
		hasValue(){
			return this.metricValue !== null && this.metricValue !== undefined;
		},
	},
	watch : {
		streamData : {
			handler(){
				this.resetWidget();
			},
			deep : true,
		},
		selectedInterval(){
			this.refresh();
		},
	},
	mounted(){
		this.loadInitialData();
	},
	beforeDestroy(){
		this.clearRefreshTimer();
	},
	methods : {
		loadInitialData(){
			if (this.unsupportedSchema) return;
			if (this.streamData.preview) {
				this.metricData = this.resolveMetricData(this.streamData.preview);
				this.metricValue = this.resolveMetricValue(this.streamData.preview);
			}
			this.refresh();
			this.startRefreshTimer();
		},
		resetWidget(){
			this.clearRefreshTimer();
			this.error = null;
			this.metricValue = 0;
			this.metricData = null;
			this.selectedInterval = this.getInitialInterval();
			this.loadInitialData();
		},
		refresh(){
			if (this.unsupportedSchema || this.loading) return;
			this.loading = true;
			this.error = null;

			DashboardChartsService.getPieEventsData(this.requestStreamData).then((response) => {
				if (response && (response.isAxiosError || response.response)) {
					this.error = this.$ml.get('dashboard_pie_events_data_error');
					return;
				}
				const data = this.unwrapResponse(response);
				this.metricData = this.resolveMetricData(data);
				this.metricValue = this.resolveMetricValue(data);
			}).catch(() => {
				this.error = this.$ml.get('dashboard_pie_events_data_error');
			}).finally(() => {
				this.loading = false;
			});
		},
		unwrapResponse(response){
			if (!response) return null;
			if (response.data && response.data.data) return response.data.data;
			if (response.data) return response.data;
			return response;
		},
		resolveMetricValue(data){
			const chart = data && data.chart ? data.chart : data;
			if (!chart) return 0;
			const value = ['requestedTotal', 'requested_total', 'total_requested', 'totalRequested', 'intervalTotal', 'interval_total', 'total']
				.map(key => chart[key])
				.find(item => item !== undefined && item !== null && item !== '');
			const numeric = Number(value || 0);
			return Number.isFinite(numeric) ? numeric : value;
		},
		resolveMetricData(data){
			return data && data.chart ? data.chart : data;
		},
		getNumericValue(value){
			const numeric = Number(value);
			return Number.isFinite(numeric) ? numeric : null;
		},
		pickNumericValue(source, keys){
			if (!source) return null;
			for (let index = 0; index < keys.length; index += 1) {
				const numeric = this.getNumericValue(source[keys[index]]);
				if (numeric !== null) return numeric;
			}
			return null;
		},
		resolveComparableTotal(data){
			const chart = data && data.chart ? data.chart : data;
			const totalKeys = [
				'total_alerts', 'totalAlerts', 'alerts_total', 'alertsTotal',
				'total_events', 'totalEvents', 'events_total', 'eventsTotal',
				'interval_total', 'intervalTotal', 'overall_total', 'overallTotal',
			];
			const metadata = chart && chart.metadata ? chart.metadata : null;
			return this.pickNumericValue(chart, totalKeys) || this.pickNumericValue(metadata, totalKeys);
		},
		getMetricSeverity(metric, value, data){
			const numericValue = this.getNumericValue(value);
			const safeValue = numericValue === null ? 0 : numericValue;

			if (metric === 'effective_operation_percentage') {
				if (safeValue >= 90) return 'healthy';
				if (safeValue >= 75) return 'warning';
				if (safeValue >= 50) return 'danger';
				return 'critical';
			}

			if (metric === 'pending_alerts') {
				const total = this.resolveComparableTotal(data);
				if (total && total > 0) {
					const ratio = safeValue / total;
					if (ratio <= 0.02) return 'healthy';
					if (ratio <= 0.10) return 'warning';
					if (ratio <= 0.25) return 'danger';
					return 'critical';
				}

				if (safeValue <= 0) return 'healthy';
				if (safeValue <= 2) return 'warning';
				if (safeValue <= 10) return 'danger';
				return 'critical';
			}

			if (metric === 'attended_alerts') {
				const total = this.resolveComparableTotal(data);
				if (total && total > 0) {
					const attendedRatio = safeValue / total;
					if (attendedRatio >= 0.90) return 'healthy';
					if (attendedRatio >= 0.75) return 'warning';
					if (attendedRatio >= 0.50) return 'danger';
					return 'critical';
				}

				if (safeValue >= 90) return 'healthy';
				if (safeValue >= 60) return 'warning';
				if (safeValue >= 30) return 'danger';
				return 'critical';
			}

			return 'neutral';
		},
		formatPercentage(value){
			const numeric = Number(value || 0);
			if (!Number.isFinite(numeric)) return '0%';
			const rounded = Math.round(numeric * 100) / 100;
			return `${rounded}%`;
		},
		getInitialInterval(){
			const config = this.info_to_component && this.info_to_component.config ? this.info_to_component.config : {};
			if (this.isConfiguredCustomInterval(config)) return 'custom';
			const preset = normalizeDashboardChartIntervalPreset(config.time && config.time.preset ? config.time.preset : 'today');
			return DASHBOARD_CHART_INTERVAL_OPTIONS.some(option => option.value === preset) ? preset : 'today';
		},
		isConfiguredCustomInterval(config = null){
			const source = config || (this.streamData && this.streamData.config ? this.streamData.config : {});
			const time = source.time || {};
			const custom = time.custom || {};
			return time.preset === 'custom' && Boolean(custom.from && custom.to);
		},
		startRefreshTimer(){
			this.clearRefreshTimer();
			this.refreshTimer = setInterval(() => this.refresh(), this.refreshSeconds * 1000);
		},
		clearRefreshTimer(){
			if (this.refreshTimer) {
				clearInterval(this.refreshTimer);
				this.refreshTimer = null;
			}
		},
	},
};
</script>

<style scoped>
.dashboard-kpi-events-widget {
	overflow: hidden;
	background: linear-gradient(135deg, #0F253D, #1565C0);
}

.dashboard-kpi-events-widget--severity-healthy {
	box-shadow: inset 0 0 0 1px rgba(129, 199, 132, 0.36), 0 12px 28px rgba(46, 125, 50, 0.20);
}

.dashboard-kpi-events-widget--severity-warning {
	box-shadow: inset 0 0 0 1px rgba(255, 213, 79, 0.42), 0 12px 28px rgba(249, 168, 37, 0.24);
}

.dashboard-kpi-events-widget--severity-danger {
	box-shadow: inset 0 0 0 1px rgba(255, 183, 77, 0.48), 0 12px 28px rgba(239, 108, 0, 0.28);
}

.dashboard-kpi-events-widget--severity-critical {
	box-shadow: inset 0 0 0 1px rgba(255, 138, 128, 0.58), 0 12px 32px rgba(198, 40, 40, 0.34);
}

.dashboard-kpi-events-widget--critical-pulse {
	animation: dashboard-kpi-critical-pulse 1.35s ease-in-out infinite;
}

.dashboard-kpi-events-widget__body {
	height: calc(100% - 24px);
	padding: 12px;
	position: relative;
}

.dashboard-kpi-events-widget__intervals {
	display: flex;
	justify-content: flex-end;
	margin-bottom: 6px;
}

.dashboard-kpi-events-widget__interval-toggle {
	border: 1px solid rgba(255, 255, 255, 0.16);
	border-radius: 8px;
	overflow: hidden;
}

.dashboard-kpi-events-widget__interval-toggle >>> .v-btn {
	min-width: 42px !important;
	padding: 0 8px !important;
	font-size: 10px;
	letter-spacing: 0.08em;
}

.dashboard-kpi-events-widget__content {
	display: flex;
	align-items: center;
	gap: 14px;
	height: calc(100% - 30px);
}

.dashboard-kpi-events-widget__icon {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 56px;
	height: 56px;
	border-radius: 16px;
	background: rgba(255, 255, 255, 0.14);
	box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.16);
}

.dashboard-kpi-events-widget__metric strong {
	display: block;
	font-size: 42px;
	line-height: 1;
	font-variant-numeric: tabular-nums;
}

.dashboard-kpi-events-widget__label,
.dashboard-kpi-events-widget__meta {
	color: rgba(255, 255, 255, 0.78);
}

@keyframes dashboard-kpi-critical-pulse {
	0%, 100% {
		filter: saturate(1);
		transform: scale(1);
	}
	50% {
		filter: saturate(1.3);
		transform: scale(1.015);
	}
}

@media (prefers-reduced-motion: reduce) {
	.dashboard-kpi-events-widget--critical-pulse {
		animation: none;
	}
}
</style>
